import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, ExternalLink, Info, Link2, Search, Undo2, UserPlus, X } from 'lucide-react'
import { ActionButton, Button, Card } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { SearchableSelect } from '@/components/pedagogy/SearchableSelect'
import { PedagogyAccessDeniedCard, PedagogyAccessErrorCard } from '@/components/pedagogy/PedagogyAccessStates'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import {
  getLegacyTrainerReviewMatchCounts,
  listLegacyTrainerReviewCandidateCities,
  listLegacyTrainerReviewCandidatePartners,
  listLegacyTrainerReviewSpecialists,
  listLegacyTrainerReviews,
  listLegacyTrainerReviewTeacherCandidates,
  resolveLegacyTrainerReviews,
  returnLegacyTrainerReviewToPending,
  type LegacyReviewMatchMode,
  type LegacyReviewResolution,
  type LegacyTrainerReviewCandidateCity,
  type LegacyTrainerReviewCandidatePartner,
  type LegacyTrainerReviewRow,
  type LegacyTrainerReviewSpecialist,
  type LegacyTrainerReviewTeacherCandidate,
} from '@/services/legacyTrainerReviewMatchService'

const PAGE_SIZE = 200

function formatDate(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-')
  return year && month && day ? `${day}.${month}.${year}` : value
}

function formatPercent(value: number): string {
  return `${Math.round(value)}%`
}

function pluralRu(value: number, one: string, few: string, many: string): string {
  const mod10 = value % 10
  const mod100 = value % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

function statusLabel(status: LegacyTrainerReviewTeacherCandidate['status']): string {
  if (status === 'working') return 'Работает'
  if (status === 'training') return 'Обучение'
  return 'Уволен'
}

function teacherAssignmentLabel(candidate: LegacyTrainerReviewTeacherCandidate): string {
  const parts = [statusLabel(candidate.status)]
  if (candidate.assignmentStartsOn) parts.push(`с ${formatDate(candidate.assignmentStartsOn)}`)
  if (candidate.assignmentEndsOn) parts.push(`до ${formatDate(candidate.assignmentEndsOn)}`)
  return parts.join(' · ')
}

function teacherTimingNote(candidate: LegacyTrainerReviewTeacherCandidate, rows: LegacyTrainerReviewRow[]): string | null {
  if (candidate.relationshipWarning === 'no_franchise_link') return 'Нет связи с франчайзи этого отсмотра — выбрать всё равно можно'
  if (candidate.relationshipWarning !== 'date_mismatch') return null
  if (candidate.assignmentStartsOn && rows.some(row => row.reviewDate < candidate.assignmentStartsOn!)) {
    return `Начало работы ${formatDate(candidate.assignmentStartsOn)} — позже даты отсмотра`
  }
  if (candidate.assignmentEndsOn && rows.some(row => row.reviewDate > candidate.assignmentEndsOn!)) {
    return `Работа завершена ${formatDate(candidate.assignmentEndsOn)} — раньше даты отсмотра`
  }
  return rows.length > 1 ? 'Даты работы не совпадают с частью выбранных отсмотров' : 'Дата работы не совпадает с датой отсмотра'
}

function InfoHint({ text }: { text: string }) {
  return (
    <button
      type="button"
      title={text}
      aria-label={text}
      className="inline-flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <Info size={13} />
    </button>
  )
}

function ResolutionChoice({ label, active, onClick, hint }: { label: string; active: boolean; onClick: () => void; hint?: string }) {
  return (
    <div className={`flex min-h-9 items-center rounded-md border ${active ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-foreground'}`}>
      <button type="button" onClick={onClick} className="min-w-0 flex-1 px-2.5 py-2 text-left text-xs font-medium hover:bg-muted/40">
        {label}
      </button>
      {hint && <span className="pr-2"><InfoHint text={hint} /></span>}
    </div>
  )
}

function resultLabel(row: LegacyTrainerReviewRow): string {
  if (row.teacherMatchResolution === 'linked') return `Привязан · ${row.linkedTeacherName ?? 'педагог'}`
  if (row.teacherMatchResolution === 'former_not_in_registry') return 'Педагог больше не работает'
  if (row.teacherMatchResolution === 'unidentified') return 'Не удалось определить'
  return 'К разбору'
}

type NameGroup = {
  key: string
  displayName: string
  rows: LegacyTrainerReviewRow[]
}

type CityGroup = {
  key: string
  cityName: string
  rowsCount: number
  names: NameGroup[]
}

function groupRows(rows: LegacyTrainerReviewRow[]): CityGroup[] {
  const cities = new Map<string, Map<string, NameGroup>>()
  for (const row of rows) {
    const cityKey = `${row.cityId ?? 'none'}:${row.cityName}`
    const nameKey = row.normalizedCoachName || row.coachName.trim().toLowerCase()
    const city = cities.get(cityKey) ?? new Map<string, NameGroup>()
    const name = city.get(nameKey) ?? { key: `${cityKey}:${nameKey}`, displayName: row.coachName.trim(), rows: [] }
    name.rows.push(row)
    city.set(nameKey, name)
    cities.set(cityKey, city)
  }

  return Array.from(cities.entries()).map(([key, names]) => {
    const groupedNames = Array.from(names.values()).sort((a, b) => a.displayName.localeCompare(b.displayName, 'ru', { sensitivity: 'base' }))
    return {
      key,
      cityName: groupedNames[0]?.rows[0]?.cityName ?? 'Город не определён',
      rowsCount: groupedNames.reduce((sum, group) => sum + group.rows.length, 0),
      names: groupedNames,
    }
  }).sort((a, b) => a.cityName.localeCompare(b.cityName, 'ru', { sensitivity: 'base' }))
}

function ReviewLine({ row, mode, onResolve, onReturn, onOpenReview, onOpenTeacher, busy }: {
  row: LegacyTrainerReviewRow
  mode: LegacyReviewMatchMode
  onResolve: (row: LegacyTrainerReviewRow) => void
  onReturn: (row: LegacyTrainerReviewRow) => void
  onOpenReview: (row: LegacyTrainerReviewRow) => void
  onOpenTeacher: (row: LegacyTrainerReviewRow) => void
  busy: boolean
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 border-t border-border/70 px-3 py-2 pl-10 text-xs first:border-t-0 md:pl-12">
      <button type="button" onClick={() => onOpenReview(row)} className="min-w-0 flex-1 text-left hover:text-primary" title="Открыть отсмотр">
        <span className="font-medium">{formatDate(row.reviewDate)}</span>
        <span className="text-muted-foreground"> · студия {row.studioNumber}</span>
        {row.directionName && <span className="text-muted-foreground"> · {row.directionName}</span>}
        <span className="text-muted-foreground"> · {formatPercent(row.qualityPercent)}</span>
      </button>
      <ActionButton tone="secondary" onClick={() => onOpenReview(row)} icon={<ExternalLink size={13} />}>Открыть</ActionButton>
      {mode === 'pending' ? (
        <ActionButton tone="primary" onClick={() => onResolve(row)}>Разобрать</ActionButton>
      ) : (
        <div className="flex shrink-0 items-center gap-1.5">
          {row.teacherMatchResolution === 'linked' && row.teacherId ? (
            <button type="button" onClick={() => onOpenTeacher(row)} className="max-w-[220px] truncate text-xs font-medium text-primary hover:underline" title={resultLabel(row)}>
              {resultLabel(row)}
            </button>
          ) : (
            <span className="max-w-[220px] truncate text-xs text-muted-foreground" title={resultLabel(row)}>{resultLabel(row)}</span>
          )}
          <ActionButton tone="secondary" disabled={busy} onClick={() => onReturn(row)} icon={<Undo2 size={13} />}>К разбору</ActionButton>
        </div>
      )}
    </div>
  )
}

export function PedagogyLegacyReviewMatchingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { loading: accessLoading, accessError, retryAccessCheck, canViewPedagogySection, canViewAllPedagogy } = usePedagogyAccess()
  const [mode, setMode] = useState<LegacyReviewMatchMode>('pending')
  const [search, setSearch] = useState('')
  const [specialistFilter, setSpecialistFilter] = useState('all')
  const [specialists, setSpecialists] = useState<LegacyTrainerReviewSpecialist[]>([])
  const [rows, setRows] = useState<LegacyTrainerReviewRow[]>([])
  const [counts, setCounts] = useState({ pendingCount: 0, resolvedCount: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [openCities, setOpenCities] = useState<Set<string>>(new Set())
  const [openNames, setOpenNames] = useState<Set<string>>(new Set())
  const [returnBusyId, setReturnBusyId] = useState<number | null>(null)
  const [modalRows, setModalRows] = useState<LegacyTrainerReviewRow[]>([])
  const [resolvedFilter, setResolvedFilter] = useState<'all' | LegacyReviewResolution>('all')

  const visibleRows = useMemo(() => {
    if (mode !== 'resolved' || resolvedFilter === 'all') return rows
    return rows.filter(row => row.teacherMatchResolution === resolvedFilter)
  }, [mode, resolvedFilter, rows])
  const groups = useMemo(() => groupRows(visibleRows), [visibleRows])
  const resolvedBreakdown = useMemo(() => ({
    linked: rows.filter(row => row.teacherMatchResolution === 'linked').length,
    former: rows.filter(row => row.teacherMatchResolution === 'former_not_in_registry').length,
    unidentified: rows.filter(row => row.teacherMatchResolution === 'unidentified').length,
  }), [rows])
  const returnTo = (location.state as { from?: string } | null)?.from ?? pedagogyPaths.teachers
  const currentReturnTo = `${location.pathname}${location.search}`

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [firstPage, nextCounts] = await Promise.all([
        listLegacyTrainerReviews({ mode, specialistFilter, search, limit: PAGE_SIZE, offset: 0 }),
        getLegacyTrainerReviewMatchCounts(specialistFilter),
      ])

      let nextRows = firstPage.rows
      for (let offset = nextRows.length; offset < firstPage.totalCount; offset += PAGE_SIZE) {
        const page = await listLegacyTrainerReviews({ mode, specialistFilter, search, limit: PAGE_SIZE, offset })
        nextRows = [...nextRows, ...page.rows]
        if (page.rows.length < PAGE_SIZE) break
      }

      setRows(nextRows)
      setCounts(nextCounts)
      setOpenCities(new Set(nextRows.length ? [`${nextRows[0].cityId ?? 'none'}:${nextRows[0].cityName}`] : []))
      setOpenNames(new Set())
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }, [mode, search, specialistFilter])

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250)
    return () => window.clearTimeout(timer)
  }, [load])

  useEffect(() => {
    if (!canViewAllPedagogy) return
    void listLegacyTrainerReviewSpecialists()
      .then(setSpecialists)
      .catch(loadError => setError(loadError instanceof Error ? loadError.message : String(loadError)))
  }, [canViewAllPedagogy])

  const toggleSet = (setter: Dispatch<SetStateAction<Set<string>>>, key: string) => {
    setter(current => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const returnToPending = async (row: LegacyTrainerReviewRow) => {
    setReturnBusyId(row.reviewId)
    setError(null)
    try {
      await returnLegacyTrainerReviewToPending(row.reviewId)
      setNotice('Отсмотр возвращён к разбору.')
      await load()
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError))
    } finally {
      setReturnBusyId(null)
    }
  }

  const openReview = (row: LegacyTrainerReviewRow) => {
    navigate(`${pedagogyPaths.reviewReport(String(row.reviewId))}?returnTo=${encodeURIComponent(currentReturnTo)}&autoPrint=0`)
  }

  if (accessError) return <PedagogyAccessErrorCard onRetry={retryAccessCheck} />
  if (!accessLoading && !canViewPedagogySection) return <PedagogyAccessDeniedCard />

  return (
    <div className="space-y-3 pb-24">
      <div>
        <button type="button" onClick={() => navigate(returnTo)} className="inline-flex items-center gap-1.5 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft size={15} />Назад
        </button>
      </div>
      <h1 className="text-xl font-bold tracking-tight">Привязка старых отсмотров</h1>

      {error && <Card className="border border-error/30 bg-error/5"><div className="flex items-start gap-2 text-sm text-error"><AlertTriangle size={17} className="mt-0.5 shrink-0" /><span>{error}</span></div></Card>}
      {notice && <Card className="border border-primary/25 bg-primary/5"><div className="flex items-center justify-between gap-2 text-sm"><span>{notice}</span><button onClick={() => setNotice(null)} className="rounded p-1 hover:bg-muted"><X size={15} /></button></div></Card>}

      <div className="border-b border-border">
        <div className="flex items-end gap-7 overflow-x-auto" role="tablist" aria-label="Статус разбора старых отсмотров">
          {([
            { key: 'pending' as const, label: 'К разбору', count: counts.pendingCount },
            { key: 'resolved' as const, label: 'Разобранные', count: counts.resolvedCount },
          ]).map(tab => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={mode === tab.key}
              onClick={() => { setMode(tab.key); setResolvedFilter('all') }}
              className={`shrink-0 border-b-2 px-0.5 pb-2.5 pt-1 text-sm font-medium transition-colors ${
                mode === tab.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 text-xs tabular-nums ${mode === tab.key ? 'text-primary' : 'text-muted-foreground'}`}>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {mode === 'resolved' && (
        <div className="overflow-x-auto rounded-xl border border-border bg-card px-2.5 py-2 scrollbar-hide">
          <div className="flex min-w-max items-center gap-1.5">
            {([
              { key: 'all' as const, label: 'Все', count: rows.length },
              { key: 'linked' as const, label: 'С привязкой к педагогу', count: resolvedBreakdown.linked },
              { key: 'former_not_in_registry' as const, label: 'Педагог не работает', count: resolvedBreakdown.former },
              { key: 'unidentified' as const, label: 'Не удалось определить', count: resolvedBreakdown.unidentified },
            ]).map(filter => (
              <button
                key={filter.key}
                type="button"
                onClick={() => setResolvedFilter(filter.key)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${resolvedFilter === filter.key ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-muted/50'}`}
              >
                {filter.label} {filter.count}
              </button>
            ))}
          </div>
        </div>
      )}

      <Card className="border border-border shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="relative min-w-0 flex-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по имени" className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </label>
          {canViewAllPedagogy && (
            <select value={specialistFilter} onChange={event => setSpecialistFilter(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary sm:w-80 lg:w-96">
              <option value="all">Все специалисты</option>
              <option value="unassigned">Без специалиста</option>
              {specialists.map(item => <option key={item.specialistUserId} value={item.specialistUserId}>{item.specialistName || item.specialistUserId}</option>)}
            </select>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map(row => <div key={row} className="h-14 animate-pulse bg-muted/20" />)}
          </div>
        </div>
      ) : groups.length === 0 ? (
        <Card><div className="py-10 text-center text-sm text-muted-foreground">{mode === 'pending' ? 'Нет отсмотров к разбору.' : 'Разобранных legacy-отсмотров пока нет.'}</div></Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="divide-y divide-border">
            {groups.map(city => {
              const cityOpen = openCities.has(city.key)
              return (
                <section key={city.key}>
                  <button type="button" onClick={() => toggleSet(setOpenCities, city.key)} className="flex min-h-[48px] w-full items-center gap-3 px-3 py-1.5 text-left transition-colors hover:bg-muted/50 md:px-4">
                    {cityOpen ? <ChevronDown size={17} className="shrink-0 text-muted-foreground" /> : <ChevronRight size={17} className="shrink-0 text-muted-foreground" />}
                    <div className="min-w-0 flex-1 truncate font-semibold">{city.cityName}</div>
                    <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                      <span>{city.names.length} {pluralRu(city.names.length, 'имя', 'имени', 'имён')}</span>
                      <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-muted px-2 py-1 font-medium tabular-nums text-foreground">{city.rowsCount}</span>
                    </div>
                  </button>
                  {cityOpen && (
                    <div className="border-t border-border bg-background">
                      <div className="divide-y divide-border">
                        {city.names.map(name => {
                          const nameOpen = openNames.has(name.key)
                          return (
                            <div key={name.key}>
                              <div className="flex min-h-11 items-center gap-2 px-3 py-1.5 pl-9 md:px-4 md:pl-11">
                                <button type="button" onClick={() => toggleSet(setOpenNames, name.key)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                                  {nameOpen ? <ChevronDown size={14} className="shrink-0 text-muted-foreground" /> : <ChevronRight size={14} className="shrink-0 text-muted-foreground" />}
                                  <span className="truncate text-sm font-semibold">{name.displayName}</span>
                                  <span className="shrink-0 text-xs text-muted-foreground">· {name.rows.length} {pluralRu(name.rows.length, 'отсмотр', 'отсмотра', 'отсмотров')}</span>
                                </button>
                                {mode === 'pending' && <ActionButton tone="secondary" onClick={() => setModalRows(name.rows)}>Разобрать все</ActionButton>}
                              </div>
                              {nameOpen && (
                                <div className="border-t border-border/70 bg-muted/10">
                                  {name.rows.map(row => (
                                    <ReviewLine
                                      key={row.reviewId}
                                      row={row}
                                      mode={mode}
                                      busy={returnBusyId === row.reviewId}
                                      onResolve={single => setModalRows([single])}
                                      onReturn={returnToPending}
                                      onOpenReview={openReview}
                                      onOpenTeacher={linked => linked.teacherId && navigate(pedagogyPaths.teacher(linked.teacherId), { state: { from: currentReturnTo } })}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        </div>
      )}

      <LegacyResolveSheet
        rows={modalRows}
        isOpen={modalRows.length > 0}
        canViewAllPedagogy={canViewAllPedagogy}
        onClose={() => setModalRows([])}
        onApplied={async message => {
          setModalRows([])
          setNotice(message)
          await load()
        }}
        onAddTeacher={() => navigate(pedagogyPaths.teacherNew)}
        onOpenReview={openReview}
      />
    </div>
  )
}

function LegacyResolveSheet({ rows, isOpen, canViewAllPedagogy, onClose, onApplied, onAddTeacher, onOpenReview }: {
  rows: LegacyTrainerReviewRow[]
  isOpen: boolean
  canViewAllPedagogy: boolean
  onClose: () => void
  onApplied: (message: string) => Promise<void>
  onAddTeacher: () => void
  onOpenReview: (row: LegacyTrainerReviewRow) => void
}) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [resolution, setResolution] = useState<LegacyReviewResolution>('linked')
  const [candidates, setCandidates] = useState<LegacyTrainerReviewTeacherCandidate[]>([])
  const [teacherId, setTeacherId] = useState<string | null>(null)
  const [teacherSearch, setTeacherSearch] = useState('')
  const [fallback, setFallback] = useState(false)
  const [fallbackCityId, setFallbackCityId] = useState<number | null>(null)
  const [fallbackPartnerNumber, setFallbackPartnerNumber] = useState<number | null>(null)
  const [cities, setCities] = useState<LegacyTrainerReviewCandidateCity[]>([])
  const [partners, setPartners] = useState<LegacyTrainerReviewCandidatePartner[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedReviewIds = useMemo(() => Array.from(selectedIds), [selectedIds])
  const selectedRows = useMemo(() => rows.filter(row => selectedIds.has(row.reviewId)), [rows, selectedIds])
  const scopeRows = selectedRows.length > 0 ? selectedRows : rows
  const targetPartnerNumbers = useMemo(() => Array.from(new Set(scopeRows.map(row => row.partnerNumber).filter((value): value is number => value != null))), [scopeRows])
  const targetCityIds = useMemo(() => Array.from(new Set(scopeRows.map(row => row.cityId).filter((value): value is number => value != null))), [scopeRows])
  const targetPartnerNumber = targetPartnerNumbers.length === 1 ? targetPartnerNumbers[0] : null
  const targetCityId = targetCityIds.length === 1 ? targetCityIds[0] : null
  const targetCityName = scopeRows[0]?.cityName ?? 'Город не определён'
  const oldName = rows[0]?.coachName?.trim() || 'Имя не указано'
  const manualScope = fallback || targetPartnerNumber == null
  const fallbackReady = fallbackCityId != null && (!canViewAllPedagogy || fallbackPartnerNumber != null)
  const candidateScopeReady = manualScope ? fallbackReady : targetPartnerNumber != null

  useEffect(() => {
    if (!isOpen) return
    setSelectedIds(new Set(rows.map(row => row.reviewId)))
    setResolution('linked')
    setTeacherId(null)
    setTeacherSearch('')
    setFallback(false)
    setFallbackCityId(null)
    setFallbackPartnerNumber(null)
    setCandidates([])
    setCities([])
    setPartners([])
    setError(null)
  }, [isOpen, rows])

  useEffect(() => {
    if (!isOpen || !manualScope || resolution !== 'linked') return
    void listLegacyTrainerReviewCandidateCities()
      .then(setCities)
      .catch(loadError => setError(loadError instanceof Error ? loadError.message : String(loadError)))
  }, [isOpen, manualScope, resolution])

  useEffect(() => {
    if (!isOpen || !manualScope || !canViewAllPedagogy || !fallbackCityId || resolution !== 'linked') {
      setPartners([])
      return
    }
    void listLegacyTrainerReviewCandidatePartners(fallbackCityId)
      .then(next => {
        setPartners(next)
        setFallbackPartnerNumber(current => current && next.some(item => item.partnerNumber === current) ? current : (next.length === 1 ? next[0].partnerNumber : null))
      })
      .catch(loadError => setError(loadError instanceof Error ? loadError.message : String(loadError)))
  }, [canViewAllPedagogy, fallbackCityId, isOpen, manualScope, resolution])

  useEffect(() => {
    if (!isOpen || resolution !== 'linked' || selectedReviewIds.length === 0 || !candidateScopeReady) {
      setCandidates([])
      return
    }

    const timer = window.setTimeout(async () => {
      setLoadingCandidates(true)
      setError(null)
      try {
        const next = await listLegacyTrainerReviewTeacherCandidates({
          reviewIds: selectedReviewIds,
          search: teacherSearch,
          cityId: manualScope ? fallbackCityId : null,
          partnerNumber: manualScope ? (canViewAllPedagogy ? fallbackPartnerNumber : null) : targetPartnerNumber,
        })
        setCandidates(next)
        setTeacherId(current => current && next.some(item => item.teacherId === current) ? current : null)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : String(loadError))
      } finally {
        setLoadingCandidates(false)
      }
    }, 180)
    return () => window.clearTimeout(timer)
  }, [canViewAllPedagogy, candidateScopeReady, fallbackCityId, fallbackPartnerNumber, isOpen, manualScope, resolution, selectedReviewIds.join(','), targetPartnerNumber, teacherSearch])

  const apply = async () => {
    if (selectedReviewIds.length === 0) {
      setError('Выберите хотя бы один отсмотр.')
      return
    }
    if (resolution === 'linked' && !teacherId) {
      setError('Выберите педагога.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const count = await resolveLegacyTrainerReviews({ reviewIds: selectedReviewIds, resolution, teacherId: resolution === 'linked' ? teacherId : null })
      await onApplied(`Обработано отсмотров: ${count}.`)
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError))
    } finally {
      setSaving(false)
    }
  }

  const cityOptions = cities.map(item => ({ value: String(item.cityId), label: item.cityName, description: `${item.teachersCount} ${pluralRu(item.teachersCount, 'педагог', 'педагога', 'педагогов')}` }))
  const partnerOptions = partners.map(item => ({ value: String(item.partnerNumber), label: item.partnerName, description: `${item.teachersCount} ${pluralRu(item.teachersCount, 'педагог', 'педагога', 'педагогов')}` }))

  const startFallback = () => {
    setFallback(true)
    setFallbackCityId(targetCityId)
    setFallbackPartnerNumber(null)
    setTeacherId(null)
    setTeacherSearch('')
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={saving ? () => {} : onClose} title="Разобрать старые отсмотры" className="sm:max-w-2xl">
      <div className="space-y-3 p-4">
        {error && <div className="rounded-md border border-error/30 bg-error/5 px-3 py-2 text-xs text-error">{error}</div>}

        <div className="rounded-md border border-border bg-muted/15 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{oldName}</div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">{targetCityName} · {rows.length} {pluralRu(rows.length, 'отсмотр', 'отсмотра', 'отсмотров')}</div>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs font-semibold"><span>Отсмотры</span><span className="text-muted-foreground">выбрано {selectedIds.size} из {rows.length}</span></div>
          <div className="max-h-36 overflow-y-auto rounded-md border border-border">
            {rows.map(row => (
              <div key={row.reviewId} className="flex items-center gap-2 border-b border-border/70 px-2.5 py-1.5 text-xs last:border-b-0 hover:bg-muted/30">
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                  <input type="checkbox" checked={selectedIds.has(row.reviewId)} onChange={() => setSelectedIds(current => { const next = new Set(current); if (next.has(row.reviewId)) next.delete(row.reviewId); else next.add(row.reviewId); return next })} />
                  <span className="min-w-0 flex-1 truncate">{formatDate(row.reviewDate)} · студия {row.studioNumber}{row.directionName ? ` · ${row.directionName}` : ''} · {formatPercent(row.qualityPercent)}</span>
                </label>
                <button type="button" onClick={() => onOpenReview(row)} className="shrink-0 text-primary hover:underline">Открыть</button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-1.5 sm:grid-cols-3">
          <ResolutionChoice label="Привязать к педагогу" active={resolution === 'linked'} onClick={() => { setResolution('linked'); setTeacherId(null) }} />
          <ResolutionChoice
            label="Педагог больше не работает"
            active={resolution === 'former_not_in_registry'}
            onClick={() => { setResolution('former_not_in_registry'); setTeacherId(null) }}
            hint="Карточки педагога нет в реестре и создавать её не нужно. Если карточка есть, даже у уволенного педагога, выберите её из списка."
          />
          <ResolutionChoice
            label="Не удалось определить"
            active={resolution === 'unidentified'}
            onClick={() => { setResolution('unidentified'); setTeacherId(null) }}
            hint="По отсмотру нельзя понять, кто был педагогом. К этому решению можно вернуться позже."
          />
        </div>

        {resolution === 'linked' && (
          <div className="space-y-2">
            {!manualScope && targetPartnerNumber != null ? (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-semibold">Педагоги франчайзи</div>
                  <div className="truncate text-[11px] text-muted-foreground">{selectedRows.length === 1 ? `Франчайзи студии ${selectedRows[0].studioNumber}` : 'Франчайзи выбранных отсмотров'} · {targetCityName}</div>
                </div>
                <div className="flex items-center gap-1">
                  <ActionButton tone="secondary" onClick={startFallback}>Другой франчайзи</ActionButton>
                  <InfoHint text="Если нужный педагог сейчас работает у другого франчайзи, найдите его здесь. Это только поиск: связи педагога и даты его работы не изменятся." />
                </div>
              </div>
            ) : (
              <div className="space-y-2 rounded-md border border-border bg-muted/10 p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <div className="text-xs font-semibold">{targetPartnerNumber == null ? 'Выберите франчайзи' : 'Другой франчайзи'}</div>
                    <InfoHint text={targetPartnerNumber == null ? 'Не удалось однозначно определить франчайзи студии. Выберите город и франчайзи вручную. Это только поиск педагога — его связи не изменятся.' : 'Если педагог сейчас работает у другого франчайзи, выберите город и франчайзи, чтобы найти его. Привязка старого отсмотра не создаёт и не меняет связи педагога.'} />
                  </div>
                  {targetPartnerNumber != null && <ActionButton tone="secondary" onClick={() => { setFallback(false); setFallbackCityId(null); setFallbackPartnerNumber(null); setTeacherId(null); setTeacherSearch('') }}>К своему франчайзи</ActionButton>}
                </div>
                <div className={`grid gap-2 ${canViewAllPedagogy ? 'sm:grid-cols-2' : ''}`}>
                  <SearchableSelect value={fallbackCityId ? String(fallbackCityId) : ''} options={cityOptions} onChange={value => { setFallbackCityId(Number(value)); setFallbackPartnerNumber(null); setTeacherId(null) }} placeholder="Выберите город" searchPlaceholder="Найти город" matchTriggerWidth />
                  {canViewAllPedagogy && <SearchableSelect value={fallbackPartnerNumber ? String(fallbackPartnerNumber) : ''} options={partnerOptions} onChange={value => { setFallbackPartnerNumber(Number(value)); setTeacherId(null) }} placeholder="Выберите франчайзи" searchPlaceholder="Найти франчайзи" disabled={!fallbackCityId} matchTriggerWidth />}
                </div>
              </div>
            )}

            {candidateScopeReady && (
              <>
                <label className="relative block">
                  <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input value={teacherSearch} onChange={event => setTeacherSearch(event.target.value)} placeholder="Поиск педагога по имени" className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-xs outline-none focus:ring-2 focus:ring-primary" />
                </label>
                <div className="max-h-60 overflow-y-auto rounded-md border border-border">
                  {loadingCandidates ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">Загружаем педагогов…</div>
                  ) : candidates.length === 0 ? (
                    <div className="px-3 py-5 text-center text-xs text-muted-foreground">{teacherSearch.trim() ? 'По этому запросу ничего не найдено.' : canViewAllPedagogy || !fallback ? 'В этом франчайзи нет педагогов.' : 'В этом городе нет педагогов.'}</div>
                  ) : candidates.map(candidate => {
                    const timingNote = teacherTimingNote(candidate, selectedRows)
                    return (
                      <button key={candidate.teacherId} type="button" onClick={() => setTeacherId(candidate.teacherId)} className={`flex w-full items-start gap-2 border-b border-border/70 px-2.5 py-2 text-left last:border-b-0 hover:bg-muted/30 ${teacherId === candidate.teacherId ? 'bg-primary/10' : ''}`}>
                        <span className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border ${teacherId === candidate.teacherId ? 'border-primary bg-primary' : 'border-border bg-background'}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-semibold">{candidate.fullName}</span>
                          <span className="block truncate text-[11px] text-muted-foreground">{teacherAssignmentLabel(candidate)}</span>
                          {timingNote && <span className="mt-0.5 block text-[11px] text-warning">{timingNote}</span>}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-2.5 py-2">
              <ActionButton tone="secondary" onClick={onAddTeacher} icon={<UserPlus size={13} />}>Добавить педагога</ActionButton>
              <div className="max-w-sm text-right text-[11px] leading-snug text-muted-foreground">Добавляйте только если педагога действительно нет в реестре. Если карточка уже есть, даже у другого франчайзи, новую создавать не нужно.</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button size="sm" variant="outline" disabled={saving} onClick={onClose}>Отмена</Button>
          <Button size="sm" disabled={saving || selectedIds.size === 0 || (resolution === 'linked' && !teacherId)} onClick={() => void apply()}>
            <Link2 size={15} className="mr-1 inline" />{saving ? 'Применяем…' : 'Применить'}
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
